import type { HTMLAttributes } from 'react';
import styled from 'styled-components';
import { SizeRatingIconLarge, SizeRatingIconMedium, SizeRatingIconSmall } from 'autogen/design-tokens/global/tokens';
import type { Size } from 'types/size';
import { breakpoint } from 'variables';
import { Typography } from '../typography';

const Container = styled.div`
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.Spacing4};

    ${({ theme }) => (theme as any).RatingContainer}
`;

const Stars = styled.div<{ $percent: number; $size: string; $starCount: number }>`
    height: ${({ $size }) => $size};
    aspect-ratio: ${({ $starCount }) => $starCount} / 1;
    background-image: linear-gradient(
        90deg,
        ${({ theme }) => theme.ComponentsRatingActive} 0,
        ${({ theme }) => theme.ComponentsRatingActive} ${({ $percent }) => $percent}%,
        ${({ theme }) => theme.ComponentsRatingInactive} ${({ $percent }) => $percent}%,
        ${({ theme }) => theme.ComponentsRatingInactive} 100%
    );
    mask-image: ${({ theme }) => `url(${theme.AssetsStarIconUrl})`};
    mask-size: contain;
    mask-repeat: repeat;
    pointer-events: none;
`;

const Count = styled(Typography.Body)`
    display: none;

    ${breakpoint.up(321)} {
        display: initial;
        color: ${({ theme }) => theme.ColorTextSecondary};
        font-weight: ${({ theme }) => theme.TypographyBodyWeightDefault};
    }
`;

export const ratingSizes = ['S', 'M', 'L'] as const satisfies Readonly<Size[]>;
export type RatingSize = (typeof ratingSizes)[number];

export type RatingStarsProps = HTMLAttributes<HTMLDivElement> & {
    rating: number;
    reviewCount?: number;
    size?: RatingSize;
    starCount?: number;
    showAvgRatingCount?: boolean;
};

function getStarSize(size: NonNullable<RatingStarsProps['size']>): string {
    const sizeMap = {
        S: SizeRatingIconSmall,
        M: SizeRatingIconMedium,
        L: SizeRatingIconLarge
    };
    return sizeMap[size];
}

export default function RatingStars({
    rating,
    size = 'S',
    reviewCount,
    starCount = 5,
    showAvgRatingCount = true,
    ...restProps
}: RatingStarsProps) {
    /*
    Rating value from backend is a float between 0 and 5.
    Therefore, it needs adjusting if we want to display fewer or more stars.
    Alternatively backend should send a percent value instead.
    */
    const ratingAdjust = starCount / 5;
    const percent = ((rating * ratingAdjust) / starCount) * 100;

    return (
        <Container {...restProps}>
            <Stars $percent={percent} $size={getStarSize(size)} $starCount={starCount} />
            {reviewCount && (
                <Count forwardedAs="span" size={size}>
                    {showAvgRatingCount && rating.toFixed(1)}
                    {` (${reviewCount})`}
                </Count>
            )}
        </Container>
    );
}
